if (!customElements.get('skin-quiz')) {
  customElements.define('skin-quiz', class SkinQuiz extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.intro = this.querySelector('[data-quiz-intro]');
      this.questions = this.querySelector('[data-quiz-questions]');
      this.results = this.querySelector('[data-quiz-results]');
      this.form = this.querySelector('[data-quiz-form]');
      this.steps = Array.from(this.querySelectorAll('[data-quiz-step]'));
      this.productPool = this.querySelector('[data-product-pool]');
      this.resultGrid = this.querySelector('[data-result-grid]');
      this.validation = this.querySelector('[data-quiz-validation]');
      this.currentStep = 0;
      this.selectedProducts = [];

      this.querySelector('[data-quiz-start]')?.addEventListener('click', () => this.start());
      this.querySelector('[data-quiz-next]')?.addEventListener('click', () => this.next());
      this.querySelector('[data-quiz-back]')?.addEventListener('click', () => this.back());
      this.querySelector('[data-quiz-restart]')?.addEventListener('click', () => this.restart());
      this.querySelector('[data-add-routine]')?.addEventListener('click', (event) => this.addRoutine(event));
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-add-product]');
        if (button) this.addSingleProduct(button);
      });
      this.form?.addEventListener('change', () => {
        this.validation.hidden = true;
      });
    }

    start() {
      this.intro.hidden = true;
      this.results.hidden = true;
      this.questions.hidden = false;
      this.currentStep = 0;
      this.showStep();
      this.scrollToQuiz();
    }

    next() {
      const step = this.steps[this.currentStep];
      const selected = step?.querySelector('input:checked');
      if (!selected) {
        this.validation.hidden = false;
        return;
      }

      this.validation.hidden = true;
      if (this.currentStep < this.steps.length - 1) {
        this.currentStep += 1;
        this.showStep();
        return;
      }

      this.buildResults();
    }

    back() {
      if (this.currentStep === 0) {
        this.questions.hidden = true;
        this.intro.hidden = false;
        this.scrollToQuiz();
        return;
      }

      this.currentStep -= 1;
      this.validation.hidden = true;
      this.showStep();
    }

    showStep() {
      this.steps.forEach((step, index) => {
        step.hidden = index !== this.currentStep;
      });

      const number = this.currentStep + 1;
      const percent = Math.round((number / this.steps.length) * 100);
      const progress = this.querySelector('[role="progressbar"]');
      this.querySelector('[data-progress-label]').textContent = `Question ${number} of ${this.steps.length}`;
      this.querySelector('[data-progress-percent]').textContent = `${percent}%`;
      this.querySelector('[data-progress-bar]').style.width = `${percent}%`;
      progress?.setAttribute('aria-valuenow', String(number));

      const nextButton = this.querySelector('[data-quiz-next]');
      nextButton.innerHTML = number === this.steps.length
        ? 'See my ritual <span aria-hidden="true">→</span>'
        : 'Next <span aria-hidden="true">→</span>';

      const legend = this.steps[this.currentStep]?.querySelector('legend');
      if (legend) {
        legend.setAttribute('tabindex', '-1');
        legend.focus({ preventScroll: true });
      }
    }

    getAnswers() {
      return Object.fromEntries(new FormData(this.form).entries());
    }

    parseList(value) {
      return (value || '')
        .split(',')
        .map((item) => item.trim().toLowerCase())
        .filter(Boolean);
    }

    scoreProduct(product, answers) {
      const skinTypes = this.parseList(product.dataset.skinTypes);
      const concerns = this.parseList(product.dataset.concerns);
      let score = 1;

      if (skinTypes.includes(answers.skin_type)) score += 7;
      else if (skinTypes.includes('all')) score += 2;
      else score -= 3;

      if (concerns.includes(answers.concern)) score += 8;
      else if (concerns.includes('all')) score += 1;

      if (answers.sensitive === 'yes') {
        score += product.dataset.sensitiveSafe === 'true' ? 6 : -10;
      } else if (answers.sensitive === 'sometimes') {
        score += product.dataset.sensitiveSafe === 'true' ? 3 : -4;
      }

      if (answers.fragrance === 'free') {
        score += product.dataset.fragranceFree === 'true' ? 7 : -12;
      }

      if (product.dataset.available !== 'true') score -= 100;
      return score;
    }

    chooseProduct(step, answers) {
      const candidates = Array.from(this.productPool.querySelectorAll(`[data-quiz-product][data-step="${step}"]`));
      return candidates
        .map((product, order) => ({ product, order, score: this.scoreProduct(product, answers) }))
        .sort((a, b) => b.score - a.score || a.order - b.order)[0]?.product || null;
    }

    buildResults() {
      const answers = this.getAnswers();
      const steps = answers.routine === 'complete'
        ? ['cleanser', 'toner', 'serum', 'moisturiser']
        : ['cleanser', 'serum', 'moisturiser'];

      while (this.resultGrid.firstChild) {
        this.productPool.appendChild(this.resultGrid.firstChild);
      }

      this.selectedProducts = steps
        .map((step) => this.chooseProduct(step, answers))
        .filter(Boolean);

      const stepNames = {
        cleanser: 'Step 1 · Cleanse',
        toner: 'Step 2 · Tone',
        serum: answers.routine === 'complete' ? 'Step 3 · Treat' : 'Step 2 · Treat',
        moisturiser: answers.routine === 'complete' ? 'Step 4 · Moisturise' : 'Step 3 · Moisturise'
      };

      this.selectedProducts.forEach((product) => {
        product.querySelector('[data-step-label]').textContent = stepNames[product.dataset.step];
        this.resultGrid.appendChild(product);
      });

      const skinLabels = {
        dry: 'Dry skin',
        oily: 'Oily skin',
        combination: 'Combination skin',
        normal: 'Balanced skin'
      };
      const concernLabels = {
        dehydration: 'Hydration',
        sensitivity: 'Sensitivity',
        blemishes: 'Blemishes',
        pigmentation: 'Uneven tone',
        aging: 'Firmness & fine lines',
        dullness: 'Radiance'
      };

      this.querySelector('[data-result-title]').textContent = `Your ${skinLabels[answers.skin_type].toLowerCase()} ritual`;
      this.querySelector('[data-result-summary]').textContent =
        `A ${answers.routine === 'complete' ? 'complete four-step' : 'simple three-step'} ritual selected to support ${concernLabels[answers.concern].toLowerCase()} while respecting your skin preferences.`;

      const profile = this.querySelector('[data-result-profile]');
      profile.innerHTML = '';
      [
        skinLabels[answers.skin_type],
        concernLabels[answers.concern],
        answers.sensitive === 'yes' ? 'Sensitive' : answers.sensitive === 'sometimes' ? 'Sometimes reactive' : 'Resilient',
        answers.fragrance === 'free' ? 'Fragrance-free' : 'Natural aromas welcome'
      ].forEach((label) => {
        const chip = document.createElement('span');
        chip.textContent = label;
        profile.appendChild(chip);
      });

      this.questions.hidden = true;
      this.results.hidden = false;
      this.querySelector('[data-cart-status]').textContent = '';
      this.scrollToQuiz();
    }

    async addItems(items) {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ items })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.description || 'We could not add this product to your cart.');
      }

      const cartResponse = await fetch(`${root}cart.js`);
      const cart = await cartResponse.json();
      document.querySelectorAll('.cart-count').forEach((counter) => {
        counter.textContent = cart.item_count;
        counter.classList.toggle('visually-hidden', cart.item_count === 0);
      });
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
      return cart;
    }

    async addSingleProduct(button) {
      const product = button.closest('[data-quiz-product]');
      if (!product?.dataset.variantId) return;

      const originalLabel = button.textContent;
      button.disabled = true;
      button.textContent = 'Adding…';
      try {
        await this.addItems([{ id: product.dataset.variantId, quantity: 1 }]);
        button.textContent = 'Added ✓';
        this.querySelector('[data-cart-status]').textContent = 'Product added to your cart.';
      } catch (error) {
        button.textContent = originalLabel;
        button.disabled = false;
        this.querySelector('[data-cart-status]').textContent = error.message;
      }
    }

    async addRoutine(event) {
      const button = event.currentTarget;
      const items = this.selectedProducts
        .filter((product) => product.dataset.available === 'true' && product.dataset.variantId)
        .map((product) => ({ id: product.dataset.variantId, quantity: 1 }));

      if (!items.length) return;
      const originalLabel = button.textContent;
      button.disabled = true;
      button.textContent = 'Adding your ritual…';
      try {
        await this.addItems(items);
        button.textContent = 'Ritual added ✓';
        this.querySelector('[data-cart-status]').textContent = `${items.length} products added to your cart.`;
      } catch (error) {
        button.textContent = originalLabel;
        button.disabled = false;
        this.querySelector('[data-cart-status]').textContent = error.message;
      }
    }

    restart() {
      while (this.resultGrid.firstChild) {
        this.productPool.appendChild(this.resultGrid.firstChild);
      }
      this.form.reset();
      this.querySelectorAll('[data-add-product]').forEach((button) => {
        const product = button.closest('[data-quiz-product]');
        const available = product?.dataset.available === 'true';
        button.disabled = !available;
        button.textContent = available ? 'Add to cart' : 'Sold out';
      });
      const addRoutineButton = this.querySelector('[data-add-routine]');
      addRoutineButton.disabled = false;
      addRoutineButton.textContent = 'Add complete ritual to cart';
      this.results.hidden = true;
      this.intro.hidden = false;
      this.currentStep = 0;
      this.selectedProducts = [];
      this.scrollToQuiz();
    }

    scrollToQuiz() {
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    }
  });
}